import { useEffect, useRef } from 'react';

/**
 * PersistentSlideAudio — one <audio> element that lives at the
 * PresentationPage level (always mounted) and plays whenever a slide
 * whose section id is in `activeIds` becomes the active slide in the
 * SlideDeck. Pauses when we leave the region.
 *
 * Why persistent?
 *   • SlideDeck lazy-mounts only the active slide, so AudioLoop /
 *     PresentationOutroAudio placed *inside* a slide only exists while
 *     that slide is on screen — the audio has no time to preload and
 *     the browser's autoplay gesture credit from earlier navigation
 *     clicks has already expired by the time play() finally fires.
 *   • Rendering the <audio> at PresentationPage level lets us
 *     preload the file once and reuse the presenter's earliest click
 *     (login / "Verstanden") as the autoplay unlock.
 *
 * The presence check uses a MutationObserver on the SlideDeck so we
 * react synchronously to slide changes.
 *
 * Behaviour:
 *   • Enter region (any id in activeIds present in DOM) → play + loop.
 *   • Leave region → pause + rewind to 0.
 *   • If play() is rejected by autoplay policy, we retry on the very
 *     next pointerdown / keydown anywhere on the window.
 *
 * @param {string} src              — audio file URL
 * @param {string[]} activeIds      — section ids that keep this track alive
 * @param {number} [volume=0.75]
 * @param {string} [dataTestId]     — passed through for tests
 */
export default function PersistentSlideAudio({
  src,
  activeIds,
  volume = 0.75,
  dataTestId,
  autoStart = true,
}) {
  const audioRef = useRef(null);

  useEffect(() => {
    const a = audioRef.current;
    if (!a || !activeIds?.length) return undefined;
    const vol = Math.max(0, Math.min(1, volume));
    a.volume = vol;

    let mo = null;
    let unlocked = false;

    const wantPlay = () => activeIds.some((id) => document.getElementById(id));

    const evaluate = () => {
      if (wantPlay()) {
        // autoStart=false: Musik startet NICHT automatisch beim Betreten —
        // sie wird explizit per Klick gestartet (SlideDeck 2-Klick-Modell).
        if (autoStart && a.paused) a.play().catch(() => { /* unlocked-on-next-gesture */ });
      } else {
        try {
          if (!a.paused) a.pause();
          a.currentTime = 0;
        } catch (_) { /* noop */ }
      }
    };

    // Safari/iOS: play() only works from a direct user-gesture call stack.
    // Prime the element SYNCHRONOUSLY inside the first gesture (play+pause
    // without awaiting — no muted-toggle, since unmuting async is itself
    // gesture-gated in WebKit). Afterwards programmatic play() from the
    // MutationObserver is permitted.
    const onGesture = () => {
      if (!unlocked) {
        unlocked = true;
        if (autoStart && wantPlay()) {
          a.play().catch(() => { unlocked = false; });
        } else {
          const p = a.play();
          a.pause();
          a.currentTime = 0;
          if (p && typeof p.catch === 'function') p.catch(() => {});
        }
      } else if (autoStart && wantPlay() && a.paused) {
        // Direct-gesture fallback: if we should be playing but are not, play now.
        a.play().catch(() => {});
      }
      // This same click may navigate INTO or OUT OF the region —
      // re-evaluate (play OR stop) after React has swapped the slide.
      setTimeout(evaluate, 220);
      setTimeout(evaluate, 600);
    };
    const opts = { capture: true, passive: true };
    window.addEventListener('pointerdown', onGesture, opts);
    window.addEventListener('keydown', onGesture, opts);
    window.addEventListener('touchstart', onGesture, opts);

    // Watch the whole body: the SlideDeck node itself can be replaced on
    // config re-renders, which would orphan an observer bound to it and
    // leave the music playing after leaving the region.
    mo = new MutationObserver(evaluate);
    mo.observe(document.body, { childList: true, subtree: true });
    evaluate();

    return () => {
      if (mo) mo.disconnect();
      window.removeEventListener('pointerdown', onGesture, opts);
      window.removeEventListener('keydown', onGesture, opts);
      window.removeEventListener('touchstart', onGesture, opts);
      try { a.pause(); } catch (_) { /* noop */ }
    };
  }, [src, activeIds, volume, autoStart]);

  return (
    <audio
      ref={audioRef}
      src={src}
      loop
      preload="auto"
      data-testid={dataTestId || 'persistent-slide-audio'}
      data-slide-persist="true"
      aria-hidden="true"
    />
  );
}
